import { useCallback, useEffect, useMemo, useState } from 'react'
import { getCategoryImageSources } from '../routing'
import type { CatalogProductFieldDefinition, CategoryRouteData } from '../types'
import CategoryPageHero from './category-page/CategoryPageHero'
import CategoryHubSections, { buildCategorySectionId } from './category-page/CategoryHubSections'
import { normalizeCategoryRouteData } from './category-page/categoryRouteDataSafety'
import { useCategoryAddToCartFeedback } from './category-page/useCategoryAddToCartFeedback'
import { useCategoryProductsPagination } from './category-page/useCategoryProductsPagination'
import CategoryProductsPagination from './category-page/CategoryProductsPagination'
import CategoryProductsGrid from './category-page/CategoryProductsGrid'
import { useCart } from '../../cart'
import { useShopI18n } from '../../i18n'
import SearchFiltersSidebar from './search-results/SearchFiltersSidebar'
import SearchFiltersSheet from './search-results/SearchFiltersSheet'
import { useSearchFiltersState } from './search-results/useSearchFiltersState'
import { useSearchResultsDerivedData } from './search-results/useSearchResultsDerivedData'
import SelectCustom from '../../components/SelectCustom'
import { CATEGORY_SORT_SELECT_OPTIONS, PAGE_SIZE_SELECT_OPTIONS } from './search-results/catalogSelectOptions'

type Props = {
  categoryRouteData: CategoryRouteData
  productFieldDefinitions: CatalogProductFieldDefinition[]
}

const CategoryPage = ({ categoryRouteData, productFieldDefinitions }: Props) => {
  const { language, toLocalizedPath } = useShopI18n()
  const { addItem, busy: cartBusy } = useCart()
  const { category, trail, children, products, productsPagination } = useMemo(
    () => normalizeCategoryRouteData(categoryRouteData),
    [categoryRouteData],
  )
  const hasChildren = children.length > 0
  // Top-level categories act as aggregate hubs. Nested category pages are
  // exact merchandising scopes and must only show products whose primary
  // category is the page category (secondary assignments stay out).
  const primaryCategoryId = category.parentCategoryId ? category._id : undefined
  const [hubPrimaryCategoryId, setHubPrimaryCategoryId] = useState<string>()
  useEffect(() => {
    setHubPrimaryCategoryId(undefined)
  }, [category._id])
  const [filtersOpen, setFiltersOpen] = useState(false)
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    selectedBrands,
    setSelectedBrands,
    selectedTypes,
    setSelectedTypes,
    priceRange,
    setPriceRange,
    numericFilters,
    setNumericFilters,
    optionFilters,
    setOptionFilters,
    sort,
    setSort,
    resetFilters,
  } = useSearchFiltersState()
  const updateSelectedBrands = (next: typeof selectedBrands | ((current: typeof selectedBrands) => typeof selectedBrands)) => {
    setSelectedBrands(next)
    setPage(1)
  }
  const updateSelectedTypes = (next: typeof selectedTypes | ((current: typeof selectedTypes) => typeof selectedTypes)) => {
    setSelectedTypes(next)
    setPage(1)
  }
  const updatePriceRange = (next: typeof priceRange | ((current: typeof priceRange) => typeof priceRange)) => {
    setPriceRange(next)
    setPage(1)
  }
  const updateNumericFilters = (next: typeof numericFilters | ((current: typeof numericFilters) => typeof numericFilters)) => {
    setNumericFilters(next)
    setPage(1)
  }
  const updateOptionFilters = (next: typeof optionFilters | ((current: typeof optionFilters) => typeof optionFilters)) => {
    setOptionFilters(next)
    setPage(1)
  }
  const updatePageSize = (next: number) => {
    setPageSize(next as typeof pageSize)
    setPage(1)
  }
  const handleHubSectionChange = useCallback((sectionId: string) => {
    const selectedChild = children.find((child) => buildCategorySectionId(child) === sectionId)
    setHubPrimaryCategoryId(selectedChild?._id)
    setPage(1)
  }, [children, setPage])
  const selectedHubCategoryId = children.some((child) => child._id === hubPrimaryCategoryId)
    ? hubPrimaryCategoryId
    : undefined
  const effectivePrimaryCategoryId = primaryCategoryId ?? (category.parentCategoryId ? undefined : selectedHubCategoryId)
  const categoryFilters = useMemo(
    () => ({ selectedBrands, selectedTypes, priceRange, numericFilters, optionFilters, sort }),
    [numericFilters, optionFilters, priceRange, selectedBrands, selectedTypes, sort],
  )
  const { cartFeedback, handleAddToCart } = useCategoryAddToCartFeedback({ addItem })
  const { productsPage, totalProductPages, pagedProducts, totalItems, productsLoading, facets, changePage } =
    useCategoryProductsPagination({
      categoryId: category._id,
      primaryCategoryId: effectivePrimaryCategoryId,
      initialProducts: products,
      initialPagination: productsPagination,
      pageSize,
      page,
      setPage,
      filters: categoryFilters,
    })
  const categoryImage = getCategoryImageSources(category, '100vw')
  const trailSlugs = trail.map((item) => item.slug)
  const {
    priceBounds,
    productTypeValues,
    brandValues,
    optionFacets,
    numericFacets,
    activeFilterCount,
  } = useSearchResultsDerivedData({
    products: pagedProducts,
    selectedBrands,
    selectedTypes,
    priceRange,
    numericFilters,
    optionFilters,
    facets: facets ?? null,
  })

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: 'var(--theme-color-background, #f8fafc)',
        color: 'var(--theme-color-text, #0f172a)',
      }}
    >
      <div className="space-y-7 pb-8 pt-0 sm:space-y-9 sm:pb-10">
        <div className="relative left-1/2 w-screen -translate-x-1/2">
          <CategoryPageHero
            category={category}
            trail={trail}
            categoryImage={categoryImage}
            toLocalizedPath={toLocalizedPath}
          />
        </div>

        <div className="mx-auto w-full max-w-[1600px] space-y-7 px-4 sm:space-y-9 sm:px-8 lg:px-12">
          {cartFeedback && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm">
              {cartFeedback}
            </div>
          )}

          {hasChildren ? (
            <section className="w-full space-y-6 sm:space-y-8">
              <header className="mx-auto max-w-3xl space-y-2 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Sottocategorie</p>
                <h2 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  Esplora le sottocategorie
                </h2>
                <p className="text-sm leading-relaxed text-slate-600">
                  Seleziona una sezione per entrare in un catalogo più specifico.
                </p>
              </header>

              <CategoryHubSections
                children={children}
                trail={trail}
                toLocalizedPath={toLocalizedPath}
                onSectionChange={category.parentCategoryId ? undefined : handleHubSectionChange}
              />
            </section>
          ) : null}

          <section className="space-y-4">
              <header className="space-y-1">
                <h2 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">Prodotti</h2>
                <p className="text-sm text-slate-600">Tutti i prodotti disponibili in questa categoria.</p>
              </header>

              {productsLoading && totalItems === 0 ? (
                <div className="shop-surface rounded-3xl border-dashed p-6 text-sm text-slate-600">
                  Caricamento prodotti in corso...
                </div>
              ) : (
                <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
                  <aside className="hidden h-fit lg:sticky lg:top-5 lg:block lg:max-h-[calc(100vh-2.5rem)] lg:overflow-y-auto">
                    <SearchFiltersSidebar
                      language={language}
                      activeFilterCount={activeFilterCount}
                      priceBounds={priceBounds}
                      priceRange={priceRange}
                      setPriceRange={updatePriceRange}
                      brandValues={brandValues}
                      selectedBrands={selectedBrands}
                      setSelectedBrands={updateSelectedBrands}
                      productTypeValues={productTypeValues}
                      selectedTypes={selectedTypes}
                      setSelectedTypes={updateSelectedTypes}
                      numericFacets={numericFacets}
                      numericFilters={numericFilters}
                      setNumericFilters={updateNumericFilters}
                      optionFacets={optionFacets}
                      optionFilters={optionFilters}
                      setOptionFilters={updateOptionFilters}
                      onResetFilters={resetFilters}
                    />
                  </aside>

                  <div className="min-w-0 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
                      <button
                        type="button"
                        onClick={() => setFiltersOpen(true)}
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 lg:hidden"
                      >
                        Filtri{activeFilterCount ? ` (${activeFilterCount})` : ''}
                      </button>
                      <span className="text-sm font-medium text-slate-600">{totalItems} prodotti</span>
                      <div className="ml-auto flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <span>Mostra</span>
                          <SelectCustom
                            value={String(pageSize)}
                            onChange={(value) => updatePageSize(Number(value))}
                            options={PAGE_SIZE_SELECT_OPTIONS}
                            ariaLabel="Numero di risultati"
                            size="md"
                            buttonClassName="min-w-[5rem] rounded-xl text-sm font-semibold"
                          />
                          <span className="hidden text-slate-500 sm:inline">risultati</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <span>Ordina</span>
                          <SelectCustom
                            value={sort}
                            onChange={(value) => {
                              setSort(value as typeof sort)
                              setPage(1)
                            }}
                            options={CATEGORY_SORT_SELECT_OPTIONS}
                            ariaLabel="Ordina prodotti"
                            size="md"
                            buttonClassName="min-w-[14rem] rounded-xl text-sm font-semibold"
                          />
                        </div>
                      </div>
                    </div>
                    {totalItems > 0 ? (
                      <CategoryProductsGrid
                        products={pagedProducts}
                        trailSlugs={trailSlugs}
                        toLocalizedPath={toLocalizedPath}
                        cartBusy={cartBusy}
                        productFieldDefinitions={productFieldDefinitions}
                        onAddToCart={(product) => {
                          void handleAddToCart(product)
                        }}
                      />
                    ) : (
                      <div className="shop-surface rounded-2xl border-dashed px-5 py-10 text-center">
                        <p className="text-base font-semibold text-slate-900">Nessun prodotto trovato</p>
                        <p className="mt-1 text-sm text-slate-600">Prova a rimuovere uno o più filtri.</p>
                      </div>
                    )}
                    <CategoryProductsPagination
                      currentPage={productsPage}
                      totalPages={totalProductPages}
                      totalItems={totalItems}
                      pageSize={pageSize}
                      onPageChange={changePage}
                    />
                  </div>
                </div>
              )}
          </section>
        </div>
      </div>
      <SearchFiltersSheet
        open={filtersOpen}
        title={language === 'en' ? 'Filters' : 'Filtri'}
        closeLabel={language === 'en' ? 'Close' : 'Chiudi'}
        onClose={() => setFiltersOpen(false)}
      >
        <SearchFiltersSidebar
          language={language}
          activeFilterCount={activeFilterCount}
          priceBounds={priceBounds}
          priceRange={priceRange}
          setPriceRange={updatePriceRange}
          brandValues={brandValues}
          selectedBrands={selectedBrands}
          setSelectedBrands={updateSelectedBrands}
          productTypeValues={productTypeValues}
          selectedTypes={selectedTypes}
          setSelectedTypes={updateSelectedTypes}
          numericFacets={numericFacets}
          numericFilters={numericFilters}
          setNumericFilters={updateNumericFilters}
          optionFacets={optionFacets}
          optionFilters={optionFilters}
          setOptionFilters={updateOptionFilters}
          onResetFilters={resetFilters}
        />
      </SearchFiltersSheet>
    </div>
  )
}

export default CategoryPage
